import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _RPCService;
// Type declarations are used at compile-time as 'headers' only (does NOT include the files)

import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { AbWebServices } from "abweb/service/AbWebServices";
import { hasDynamicParameters } from "abweb/service/rpc/CacheBustConfig";
import { MethodType } from "abweb/service/rpc/MethodType";
import { OutstandingRequestCounterImpl } from "abweb/service/rpc/OutstandingRequestCounterImpl";
import { StreamingRPCServiceImpl } from "abweb/service/rpc/StreamingRPCServiceImpl";
import { ClientSessionService } from "abweb/service/session/ClientSessionService";
import { AbInitioStaleDynamicCacheBustParametersError } from "abweb/service/utils/rpc/AbInitioStaleDynamicCacheBustParametersError";
import { FetchUtils } from "abweb/service/utils/rpc/FetchUtils";
import { UnexpectedResponseContentError } from "abweb/service/utils/rpc/UnexpectedResponseContentError";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { TSOGUtils } from "abweb/utils/json/TSOGUtils";
import { LocaleUtils } from "abweb/utils/LocaleUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ObjectUtils } from "abweb/utils/ObjectUtils";
import { ProfileUtils } from "abweb/utils/profile/ProfileUtils";
import { ManualPromise } from "abweb/utils/PromiseUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { URLUtils } from "abweb/utils/URLUtils";
import { action, makeObservable, observable } from "mobx";
const LOG = LogUtils.getLogger(__filename);
export let RPCService = (_class = (_RPCService = class RPCService {
  constructor() {
    // Do via delegation and not inheritance!
    _defineProperty(this, "streamingRPCService", new StreamingRPCServiceImpl());
    _defineProperty(this, "outstandingRequestCounter", new OutstandingRequestCounterImpl());
    _initializerDefineProperty(this, "_clientSessionId", _descriptor, this);
    // Use setObservableClientSessionId to set it
    _defineProperty(this, "suppressErrorsOnShutdown", false);
    /**
     * Determine error handling behavior when the user session is lost (disabled or invalidated).
     * Typically, application will configure this to use either the StatefulServerAuthenticationStrategy (mt-backed products)
     * or StatelessServerAuthenticationStrategy (db-backed products mhub and cc).
     *
     * Defaults to simply rethrowing the error and letting 'last chance error handler' deal with it (ugly), as no UI
     * (react or gelato) is available.
     */
    _defineProperty(this, "authenticationStrategy", {
      handleLostSession(lostSessionErrorContext) {
        throw lostSessionErrorContext.error;
      }
    });
    makeObservable(this);
  }
  setShuttingDown() {
    this.suppressErrorsOnShutdown = true;
  }

  //////////   Core RPC invocation methods  //////////

  /**
   * @inheritdoc
   */
  invokeVoid(appRelativeUrl, namedParams, init, countRequest = true, autoAuthRetry = true) {
    const that = this;
    const {
      absUrl,
      reqInit
    } = this.buildFetchArgs("Invoking void rpc: ", appRelativeUrl, namedParams, init);
    const fetchConfig = this.buildFetchConfig(appRelativeUrl);

    // Do NOT just lazily attach the catch as that would be a 'dynamic' catch with retraction issues...
    return autoAuthRetry ? FetchUtils.fetchVoid(absUrl, reqInit, fetchConfig, countRequest ? this.outstandingRequestCounter : null).catch(that.buildAuthRetryHandlerFn(() => that.invokeVoid(appRelativeUrl, namedParams, init, false, autoAuthRetry))) : FetchUtils.fetchVoid(absUrl, reqInit, fetchConfig, countRequest ? this.outstandingRequestCounter : null);
  }

  /**
   * @inheritdoc
   */
  // Note that all invokeRet* methods funnel through this method (so auth retry logic works for all)
  // except for: invokeVoid, invokeRetXml, invokeRetBlob, invokeUploadBlob
  invokeRetJSON(appRelativeUrl, namedParams, init, countRequest = true, cacheBustConfig) {
    return this.invokeWithCacheBustSupport(namedParams, init, (namedParams, requestInit) => {
      const fetchArgs = this.buildFetchArgs("Invoking json rpc: ", appRelativeUrl, namedParams, requestInit);
      const fetchPromise = FetchUtils.fetchAndParseJson(fetchArgs.absUrl, fetchArgs.reqInit, this.buildFetchConfig(appRelativeUrl), countRequest ? this.outstandingRequestCounter : null);
      return fetchPromise.catch(this.buildAuthRetryHandlerFn(() => {
        return this.invokeRetJSON(appRelativeUrl, namedParams, init, false, cacheBustConfig);
      }));
    }, cacheBustConfig);
  }

  // invokeRetXml is used by mhub to call legacy *.do actions that return xml payloads

  /**
   * @inheritDoc
   */
  invokeRetXml(appRelativeUrl, namedParams, methodType) {
    const reqInitArg = this.configureRequestInit(methodType);
    // Posts are always form-based, and mhub *.do response is expected to be text/xml.
    // The default RequestInit created for a POST is always a json request payload and response,
    // so instead make it a form-based request and a text/xml response
    if (methodType === MethodType.POST) {
      FetchUtils.setHeader(reqInitArg, "Content-Type", FetchUtils.MEDIA_TYPE_FORM + ";charset=UTF-8");
    }
    // For now, only know of use case where rpc returns resp body as xml string (POST or GET)
    FetchUtils.setHeader(reqInitArg, "Accept", FetchUtils.MEDIA_TYPE_XML);
    const that = this;
    const {
      absUrl,
      reqInit
    } = this.buildFetchArgs("Invoking legacy string rpc: ", appRelativeUrl, namedParams, reqInitArg, false);
    const fetchConfig = this.buildFetchConfig(appRelativeUrl);

    // If receive response with 'bad content-type' then throw, as not worth parsing.
    // Can happen when an app server redirected to a login.html page (but prefer app server to throw 401 instead)...
    // As such, this is treated as a re-authentication workflow trigger (see isAuthenticationError)
    const validateXmlResponseFn = function (resp) {
      const contentType = resp.headers.get("Content-Type");
      // Treat no content-type as ok as believe a lot of the legacy xml calls invoke methods that return void...
      if (contentType != null && contentType.indexOf("text/xml") < 0) {
        const errMsg = LocaleUtils.safeResourceLookup(() => AbWebResources.rpcServiceError_unexpectedResponseHeader(FetchUtils.MEDIA_TYPE_XML, contentType), `Expected response header 'Content-Type' to either be empty or to contain '${FetchUtils.MEDIA_TYPE_XML}' but was: '${contentType}'`);
        throw new UnexpectedResponseContentError(errMsg);
      }
    };
    return FetchUtils.fetchText(absUrl, reqInit, fetchConfig, null, validateXmlResponseFn).catch(that.buildAuthRetryHandlerFn(() => that.invokeRetXml(appRelativeUrl, namedParams, methodType)));
  }

  /**
   * @inheritdoc
   */
  invokeRetObj(appRelativeUrl, namedParams, fnCtor, init, countRequest = true) {
    return this.invokeAndParse(appRelativeUrl, namedParams, json => TSOGUtils.decodeObject(json, fnCtor), init, countRequest);
  }

  /**
   * @inheritdoc
   */
  invokeRetObjArray(appRelativeUrl, namedParams, fnCtor, init, countRequest) {
    return this.invokeAndParse(appRelativeUrl, namedParams, json => TSOGUtils.decodeObjectArray(json, fnCtor), init, countRequest);
  }

  /**
   * @inheritdoc
   */
  invokeRetObjMap(appRelativeUrl, namedParams, fnCtor, init) {
    return this.invokeAndParse(appRelativeUrl, namedParams, json => TSOGUtils.decodeObjectMap(json, fnCtor), init);
  }

  /**
   * @inheritdoc
   */
  invokeRetEnum(appRelativeUrl, namedParams, enumType, init) {
    return this.invokeAndParse(appRelativeUrl, namedParams, json => TSOGUtils.decodeEnum(json, enumType), init);
  }

  /**
   * @inheritdoc
   */
  invokeRetEnumArray(appRelativeUrl, namedParams, enumType, init) {
    return this.invokeAndParse(appRelativeUrl, namedParams, json => TSOGUtils.decodeEnumArray(json, enumType), init);
  }

  /**
   * @inheritdoc
   */
  invokeRetBlob(appRelativeUrl, namedParams, init, countRequest = true) {
    const that = this;
    const {
      absUrl,
      reqInit
    } = this.buildFetchArgs("Invoking blob rpc: ", appRelativeUrl, namedParams, init, false);
    const fetchConfig = this.buildFetchConfig(appRelativeUrl);
    return FetchUtils.fetchBlob(absUrl, reqInit, fetchConfig, countRequest ? this.outstandingRequestCounter : null).catch(that.buildAuthRetryHandlerFn(() => that.invokeRetBlob(appRelativeUrl, namedParams, init, false)));
  }

  /**
   * @inheritdoc
   */
  invokeUploadBlob(appRelativeUrl, toUpload, countRequest = true) {
    const that = this;
    const reqInit = {
      method: "post",
      body: toUpload,
      headers: {}
    };
    const absUrl = this.configureUrlAndParams(appRelativeUrl, null, reqInit);
    const fetchConfig = this.buildFetchConfig(appRelativeUrl);
    LOG.debug("Invoking upload blob rpc: " + absUrl);
    return FetchUtils.fetchVoid(absUrl, reqInit, fetchConfig, countRequest ? this.outstandingRequestCounter : null).catch(that.buildAuthRetryHandlerFn(() => that.invokeUploadBlob(appRelativeUrl, toUpload, false)));
  }

  //////////   Helper methods to process arguments, url, RequestInit, ... data structures //////////

  async invokeWithCacheBustSupport(namedParameters, init, executeRPC, cacheBustConfig) {
    const requestInit = this.configureRequestInit(init);
    if (!cacheBustConfig) {
      return executeRPC(namedParameters, requestInit);
    }
    if (requestInit.method !== "get") {
      throw new Error(RPCService.getOnlyGetSupportedForCacheBustMessage());
    }
    // Detected by FetchUtils, and eventually PreventCachingFilter.java
    requestInit.cache = cacheBustConfig.cachingBehavior ?? "default";
    const cacheBustParameters = await cacheBustConfig.fetchParameters();
    if (!hasDynamicParameters(cacheBustParameters)) {
      return executeRPC({
        ...namedParameters,
        ...ObjectUtils.mergeSorted(cacheBustParameters.staticParameters)
      }, requestInit);
    }
    const staticCacheBustParameters = "staticParameters" in cacheBustParameters ? cacheBustParameters.staticParameters : undefined;
    let localDynamicCacheBustParameters = cacheBustParameters.dynamicParameters;
    while (true) {
      try {
        return await executeRPC({
          ...namedParameters,
          ...ObjectUtils.mergeSorted(staticCacheBustParameters, localDynamicCacheBustParameters)
        }, requestInit);
      } catch (e) {
        if (ClassUtils.isInstanceOf(e, AbInitioStaleDynamicCacheBustParametersError)) {
          const remoteDynamicCacheBustParameters = e.currentRemoteDynamicCacheBustParameterValues;
          if (ObjectUtils.deepCompare(localDynamicCacheBustParameters, remoteDynamicCacheBustParameters)) {
            throw new Error(RPCService.getUnexpectedDynamicParameterEqualityMessage(remoteDynamicCacheBustParameters));
          }
          localDynamicCacheBustParameters = remoteDynamicCacheBustParameters;
        } else {
          throw e;
        }
      }
    }
  }
  static getUnexpectedDynamicParameterEqualityMessage(dynamicParameters) {
    const serialized = JSON.stringify(dynamicParameters);
    return LocaleUtils.safeResourceLookup(() => AbWebResources.rpcServiceError_unexpectedDynamicParameterEquality(serialized), () => `Local and remote dynamic cache bust parameters should differ, but both equal '${serialized}'`);
  }
  static getOnlyGetSupportedForCacheBustMessage() {
    return LocaleUtils.safeResourceLookup(AbWebResources.rpcServiceError_cachedResourcesRequireGet, "Requests for cacheable resources must be of type GET");
  }

  /**
   * @inheritdoc
   */
  openDownloadLink(appRelativeUrl, namedParams, untilComplete) {
    const downloadLink = this.makeAbsoluteGetUrl(appRelativeUrl, namedParams);
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = downloadLink;
    iframe.onload = () => {
      /*
       * Chrome will not fire the onload event if the response's content-disposition is set to
       * 'attachment', and that is the expected content-disposition here, since we're dealing with downloads.
       * Seemingly, then, without external knowledge, there is no way to know in Chrome when the file has been fully
       * downloaded, i.e. when it's safe to remove the window outside this event handler.
       *
       * So, if onload is not invoked, specifically in Chrome, and if we don't have access to a download completion
       * promise (currently implemented with polling) we will accumulate one injected iframe per download click until
       * the page is reloaded. This is a small subset of possible outcomes, but is it an issue?
       *
       * Lastly, note that iFrame.onload is overloaded such that it is invoked in the event of a successful or failed
       * load. As such iFrame.onerror is never invoked.
       */
      setTimeout(() => {
        // Take care to remove iframe after file has been fully downloaded
        // Josh says: Firefox gets mad if I remove the iframe right away, but it is OK after a little
        iframe.remove();
      }, 100);
    };

    /*
     * However, regardless of the browser, if we have access to a polling-based
     * promise whose resolution indicates the completion of the actual download, we can
     * remove the iframe once whether its promise is resolved or rejected. (Fortunately, iframe.remove() appears to be
     * idempotent, so it's not an issue that the above removal in the event handler's set timeout might precede this
     * call, which (if at all) would happen in a non-Chrome browser).
     */
    untilComplete?.finally(() => iframe.remove());
    const containingNode = document.getElementById("appGoesHere");
    containingNode.appendChild(iframe);
  }

  /**
   * This will build the args needed to invoke window.fetch (via FetchUtils), including
   * adding the clientSessionId to the url when appropriate.
   */
  buildFetchArgs(invokeMsg, appRelativeUrl, namedParams, init, logBody = true) {
    const reqInit = this.configureRequestInit(init);
    const absUrl = this.configureUrlAndParams(appRelativeUrl, namedParams, reqInit);
    LOG.debug(invokeMsg + absUrl + logBody ? ", body: " + reqInit.body : "");
    return {
      absUrl,
      reqInit
    };
  }
  buildFetchConfig(appRelativeUrl) {
    // If include url args or anchors then there are an infinite number of url stat rows (blows out profile memory)
    // So aggregate profiling on the rpc url with query string and anchor stripped out.

    // first ? or # terminates the root of the url
    const urlRootMatches = appRelativeUrl.match(/(.*)[?#]/);
    const urlWithNoParamValues = urlRootMatches ? urlRootMatches[1] : appRelativeUrl;
    return {
      profileRoot: RPCService.PROFILE_ROOT_LABEL + urlWithNoParamValues
    };
  }
  invokeAndParse(appRelativeUrl, namedParams, decodeJSON, init, countRequest = true) {
    // parse json into RetT
    const fetchConfig = this.buildFetchConfig(appRelativeUrl);
    return this.invokeRetJSON(appRelativeUrl, namedParams, init, countRequest).then(this.profileDecodeJSON(fetchConfig.profileRoot, decodeJSON));
  }
  profileDecodeJSON(profileRoot, decodeJSON)
  // Wrap original decodeJSON method with profiling logic if profiling enabled.
  {
    if (!ProfileUtils.enabled) {
      return decodeJSON;
    }
    return json => {
      // :parseToObject chosen to alphabetically come after parseToJson so that
      // rpc profiling ordered by label shows: invoke, parseToJson, parseToObject, responseLength
      return ProfileUtils.elapsedSync(profileRoot + ":parseToObject", () => decodeJSON(json));
    };
  }

  /** return function that will try to re-establish authentication and then retry original rpc  */
  buildAuthRetryHandlerFn(retryRPC) {
    const that = this;
    // This returned function is the 'catch block handler' function.
    // Either returns a promise to continue the rpc promise chain, OR throws an error
    return async function (error) {
      if (that.suppressErrorsOnShutdown) {
        // await a promise that will never resolve, which prevents error from propagating any further
        // This suppresses all errors.  However, do not suppress all subsequent rpcs from being invoked, as
        // some may get through before login happens.  See EI-15555   .  Those that arrive after logout get a
        // 500 with 'abweb.error.noSession' indicating that session was pulled out from under client id.
        await new ManualPromise().promise; // never resolves
      }
      const lostSessionErrorContext = await AbWebServices.authenticationService.determineLostSessionError(error);
      if (lostSessionErrorContext == null) {
        // not a lost session error, so not handled here.  Rethrow.
        throw error;
      }
      return that.authenticationStrategy.handleLostSession(lostSessionErrorContext)
      // If handleLostSession returns gracefully, the rpc will be retried
      // If handleLostSession throws, the rpc will not be retried.  It can throw an 'alreadyHandledError' if need
      // to prevent the 'last chance error handler' from displaying the error.
      .then(() => {
        return retryRPC();
      });
    };
  }
  configureRequestInit(init) {
    // Returns reqInit with method specified (get|post|put|patch|delete)
    const reqInit = this.getOrBuildRequestInit(init);
    if (!reqInit.headers) {
      reqInit.headers = {};
    }
    if (reqInit.method === "post" || reqInit.method === "put" || reqInit.method === "patch") {
      // content-type could be form or json payload, default to json if not specified
      FetchUtils.setHeaderIfUnset(reqInit, "Content-Type", FetchUtils.MEDIA_TYPE_JSON);
    }
    // Accept as wide variety of return formats to consider (blob, json, void, null, xml, ...)
    // Response-related headers:
    // default response format is json
    FetchUtils.setHeaderIfUnset(reqInit, "Accept", FetchUtils.MEDIA_TYPE_JSON);
    return reqInit;
  }

  /** @returns init with method specified (defaults to post if not specified) */
  getOrBuildRequestInit(init) {
    // Post is the default as can handle object arguments (GETs only handle simple 'primitive' args)
    // Work around that can't use instanceof on an interface or enum.  Instead, cast enum to number and work with that.
    // See http://stackoverflow.com/questions/29706609/typescript-how-to-add-type-guards-for-enums-in-union-types
    //let fixedInit: RequestInit|number = <RequestInit|number> init;
    const requestInit = this.toRequestInit(init ?? MethodType.POST);
    if (!requestInit.method) {
      requestInit.method = "post";
    }
    return requestInit;
  }
  toRequestInit(init) {
    if (typeof init === "number") {
      switch (init) {
        case MethodType.GET:
          return {
            method: "get"
          };
        case MethodType.POST:
          return {
            method: "post"
          };
        case MethodType.DELETE:
          return {
            method: "delete"
          };
        case MethodType.PUT:
          return {
            method: "put"
          };
        case MethodType.PATCH:
          return {
            method: "patch"
          };
        default:
          // This 'default' block verifies that there is a case statement for every possible enum value,
          // or else you'll get a compile error.
          const unreachable = init;
      }
    }
    return init;
  }

  /** Returns calculated Url (included clientSessionId and any url args) AND updates reqInit.body appropriately */
  configureUrlAndParams(appRelativeUrl, namedParams, reqInit) {
    const isDirectProxy = appRelativeUrl.startsWith("/../");
    let url = URLUtils.getAbsoluteUrl(appRelativeUrl);
    /*
     * If this is a direct proxy, the ProxyRPCService will already be processing the client session based on what
     * it negotiated directly with the proxied application.
     */
    if (!isDirectProxy && !FetchUtils.isCachingEnabled(reqInit.cache)) {
      url = this.processClientSession(url);
    }
    if (reqInit.method === "post" || reqInit.method === "put" || reqInit.method === "patch") {
      const contentType = FetchUtils.getHeader(reqInit, "Content-Type");
      if (!isDirectProxy) {
        this.processCSRFToken(reqInit);
      }
      if (namedParams) {
        // Handles path variables and json args.
        // Note that url can have path variables with init that are populated from namedParams.
        const urlAndJsonArgs = this.buildBodyArgs(url, namedParams, contentType);
        url = urlAndJsonArgs.url;

        /*
         * OpenApi generated calls have already populated the reqInit.body, so do NOT clobber it.
         * OpenApi still wants URL path parameter resolution which is implemented as part of JsonArg processing.
         * TODO: If OpenApi request processing requires more customization, we should lift it out as a top level
         * IRPCService entry point and plumb through URL processing without the JsonArg processing so we don't have
         * to have this hack.
         */
        if (!reqInit.body) {
          reqInit.body = urlAndJsonArgs.body;
        }
      }
      if (StringUtils.contains(contentType, FetchUtils.MEDIA_TYPE_MULTIPART_FORM)) {
        // Forces fetch to create default multipart content-type with specific 'boundary' value
        // See https://stackoverflow.com/questions/39280438/fetch-missing-boundary-in-multipart-form-data-post
        FetchUtils.removeHeader(reqInit, "Content-Type");
      }
    } else if (reqInit.method === "get" || reqInit.method === "delete") {
      if (reqInit.method === "delete") {
        if (!isDirectProxy) {
          this.processCSRFToken(reqInit);
        }
      }
      if (namedParams) {
        // Treats null and undefined values as empty string
        url = URLUtils.addParamsToUrl(url, namedParams); // Handles Path Variable and Request Params
      }
    } else {
      const errMsg = LocaleUtils.safeResourceLookup(() => AbWebResources.rpcServiceError_unhandledRpcMethodType(String(reqInit.method)), () => `Only handle 'post', 'put', 'patch', 'get' and 'delete' rpc method types but received: '${reqInit.method}'`);
      LOG.error(errMsg);
      throw new Error(errMsg);
    }
    return url;
  }

  // Helper method only used by RPCService to add clientSessionId to params
  processClientSession(absoluteUrl) {
    if (this._clientSessionId) {
      absoluteUrl = URLUtils.appendQueryParamsToUrl(absoluteUrl, {
        [ClientSessionService.CLIENT_SESSION_ID]: this._clientSessionId
      });
    }
    return absoluteUrl;
  }
  processCSRFToken(req) {
    const csrfToken = URLUtils.readCookie(RPCService.CSRF_TOKEN);
    if (csrfToken) {
      // If csrfToken doesn't exist, probably forgot to invoke 'api/service/csrfToken' via index.html script tag
      // Note xspec tests do NOT need it as cookies aren't correctly passed through karma proxy,
      // and as there is no cookie, the csrf prevention filter does nothing (passes everything through)
      FetchUtils.setHeader(req, RPCService.CSRF_TOKEN, csrfToken);
    }
  }

  /**
   *  For posts, there are two possibilities for sending params:
   *  <ol>
   *  <li>body: if content-type=application/json then body is tsog object holding tsog encoded param values.
   *  <ul>
   *     <li>endpoint uses @JsonArg to pull params from json body
   *     <li>For body posts, the current server-side parser (based on @JsonArg) assumes each argument is a valid self-contained
   *     json expression (so there are no '@id' that are 'shared' between args).
   *  </ul>
   *  </li>
   *
   *  <li> form: vanilla (xml) where response.body contains a form query string  with primitive values only
   *  <ul>
   *    <li>endpoint uses @RequestParam to pull param values from request form body (and NOT @JsonArg)
   *  </ul>
   *  </li>
   *
   *  <li> multipartform: The response.body contains a FormData instance containing File and primitive values only
   *  <ul>
   *    <li>endpoint uses @RequestParam to pull primitive param values from request form body (and NOT @JsonArg)
   *    <li>endpoint uses @RequestPart to pull File param values from request form body (and NOT @JsonArg)
   *  </ul>
   *  </li>
   *  </ol>
   */
  buildBodyArgs(url, namedParams, contentType) {
    // Note that on server, params are deserialized individually, so the ids/references for each param must
    // be self-contained (so stringify each param separately)
    if (namedParams == null || namedParams.size === 0) {
      return {
        url,
        body: undefined
      };
    }
    if (StringUtils.contains(contentType, FetchUtils.MEDIA_TYPE_JSON)) {
      return this.buildAppJsonArgs(url, namedParams); // 1
    }
    Assert.assert(StringUtils.contains(contentType, FetchUtils.MEDIA_TYPE_FORM) || StringUtils.contains(contentType, FetchUtils.MEDIA_TYPE_MULTIPART_FORM));
    // if legacyJson is false then encodeTSOGParams must be false
    // const encodeTSOGParams = StringUtils.contains(accept, FetchUtils.MEDIA_TYPE_JSON);
    const isVanillaForm = StringUtils.contains(contentType, FetchUtils.MEDIA_TYPE_FORM);
    return isVanillaForm ? this.buildFormArgs(url, namedParams) // 2
    : this.buildMultipartFormArgs(url, namedParams); // 3
  }

  /**
   * @param relativeUrl
   * @param namedParams parameter values MUST BE PRIMITIVES (string|number|boolean) for form submissions
   * @return url with path substitution from namedParams, and the remaining namedParams are url encoded into
   * a form payload (url-encoded query string of name-value pairs) for application/x-www-form-urlencoded
   */
  buildFormArgs(relativeUrl, namedParams) {
    let {
      url,
      remainingFormParams
    } = this.buildFormUrlAndArgs(relativeUrl, namedParams);
    // calc query string fragment if there are any tsog params
    let queryString = "";
    if (Object.keys(remainingFormParams).length) {
      // Form parameters should be url encoded
      queryString = URLUtils.appendQueryParamsToUrl("", remainingFormParams);
      queryString = queryString.substring(1); // Strip off leading '?'
    }
    return {
      url,
      body: queryString
    };
  }
  buildMultipartFormArgs(relativeUrl, namedParams) {
    // Can NOT invoke buildFormUrlAndArgs with named params that contain 'File' type, so separate
    const nonFileParams = {};
    const formData = new FormData();
    for (const [paramName, paramVal] of Object.entries(namedParams)) {
      if (ClassUtils.isInstanceOf(paramVal, File)) formData.append(paramName, paramVal);else nonFileParams[paramName] = paramVal;
    }

    // Process rest of params, which can also influence url.  Also coerces primitives into 'string'
    const {
      url,
      remainingFormParams
    } = this.buildFormUrlAndArgs(relativeUrl, nonFileParams);
    for (const [paramName, paramVal] of Object.entries(remainingFormParams)) {
      formData.append(paramName, paramVal);
    }
    return {
      url,
      body: formData
    };
  }

  /**
   * @param url
   * @param namedParams MUST ONLY HOLD PRIMITIVE VALUES (string, number, boolean)
   * @return url with path substitution from namedParams,
   * and the remaining namedParams are tsog encoded into the tsogArgs map
   */
  buildFormUrlAndArgs(url, namedParams) {
    const {
      injectedUrl,
      unusedParams
    } = URLUtils.calcUrl(url, namedParams);
    const formArgs = {};
    for (const [paramName, paramVal] of Object.entries(unusedParams)) {
      formArgs[paramName] = URLUtils.calcFormParamValue(paramName, paramVal, url);
    }
    return {
      url: injectedUrl,
      remainingFormParams: formArgs
    };
  }

  /**
   * Build url and tsog json body payload from templatized url and namedParams args.
   * Named params are injected into the url templated if they are used, otherwise they are added to the json payload
   * via TSOGUtils.stringify.
   */
  buildAppJsonArgs(url, namedParams) {
    const {
      injectedUrl,
      unusedParams
    } = URLUtils.calcUrl(url, namedParams);
    let jsonBody = "";
    let first = true;
    for (const [paramName, rawParamVal] of Object.entries(unusedParams)) {
      // CAT-945: undefined in not part of json spec, so converts undefined vals in obj graph to null
      const paramJsonVal = TSOGUtils.stringify(rawParamVal);
      if (first) {
        first = false;
      } else {
        jsonBody += ", ";
      }
      jsonBody += `"${paramName}": ${paramJsonVal}`;
    }
    return {
      url: injectedUrl,
      body: "{" + jsonBody + "}"
    };
  }

  //////////   Start ClientSessionManager implementation  //////////
  // Note that even non-polling (aka streaming) rpcs need clientSessionId support (see buildFetchArgs),
  // so do NOT fold into StreamingRPCService!

  get clientSessionId() {
    return this._clientSessionId;
  }
  setObservableClientSessionId(clientSessionId) {
    if (clientSessionId !== this._clientSessionId) {
      // clientSessionId acts as an observable semaphore.
      // Streaming apis (including via directProxy) will unsubscribe when null,
      // and resubscribe when not null. See AbStreamingHooks for more detail.
      this._clientSessionId = clientSessionId;
      window.chrome?.webview?.postMessage?.("clientSessionId: " + clientSessionId);
    }
  }

  // TODO: Consider calling this "setClientSessionConfiguration" and take a "ClientSessionConfiguration" object.
  // Note that this method may be called multiple times during an applications lifetime, as it may need to reconnect
  setClientSessionId(clientSessionId, pollConfiguration) {
    this.setObservableClientSessionId(clientSessionId);
    let idleTimeout = pollConfiguration.idleTimeout;
    let sessionTimeout = pollConfiguration.sessionTimeout;
    let isWSDisabled = pollConfiguration.isWSDisabled;
    LOG.debug("isWSDisabled: " + isWSDisabled);
    this.streamingRPCService.lazyCreateTaskPoller(this, idleTimeout, sessionTimeout, isWSDisabled);
  }
  closeClientSession() {
    /*
     * taskPoller may still be undefined if setClientSessionId never got called.
     * If this happens (session/init failed), we still want the user to be able to successfully log out.
     */
    LOG.debug(`Closing Client Session for clientSessionId=${this._clientSessionId}`);
    this.streamingRPCService.stopPolling();
    this.setObservableClientSessionId(null);
  }

  /**
   * @inheritdoc
   */
  makeAbsoluteGetUrl(appRelativeUrl, namedParams = null) {
    const {
      absUrl: absoluteUrl
    } = this.buildFetchArgs("Making absolute url: ", appRelativeUrl, namedParams, MethodType.GET, false);
    return absoluteUrl;
  }

  /** @inheritdoc */
  makeGetUrl(relativeUrl) {
    return this.processClientSession(relativeUrl);
  }

  //////////   StreamingRPCService implementation (via delegation) for polling-based RPC Support   //////////

  getIsReady() {
    return this.streamingRPCService.getIsReady();
  }
  get taskPollerId() {
    return this.streamingRPCService.taskPollerId;
  }
  invokeAsync(appRelativeUrl, namedParams, pollResponseParser, messageHandlers, init) {
    return this.streamingRPCService.invokeAsync(appRelativeUrl, namedParams, pollResponseParser, messageHandlers, init);
  }
  invokeAsyncVoid(appRelativeUrl, namedParams, messageHandlers, init) {
    return this.streamingRPCService.invokeAsyncVoid(appRelativeUrl, namedParams, messageHandlers, init);
  }
  invokeView(appRelativeUrl, namedParams, fullViewHandler, updateViewHandler, init) {
    return this.streamingRPCService.invokeView(appRelativeUrl, namedParams, fullViewHandler, updateViewHandler, init);
  }

  //////// End delegate async-related implementations to internal pollingRPCService /////////////
}, _defineProperty(_RPCService, "PROFILE_ROOT_LABEL", "RPCService:"), _defineProperty(_RPCService, "CSRF_TOKEN", "csrfToken"), _RPCService), _descriptor = _applyDecoratedDescriptor(_class.prototype, "_clientSessionId", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "setObservableClientSessionId", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setObservableClientSessionId"), _class.prototype), _class);
//# sourceMappingURL=RPCService.js.map
